import type { ContenidoAyuda, ItemAyuda } from "@/ui/AyudaVista";

/**
 * Ayuda de Configuración: la de la página (cómo está organizada) y la de cada
 * sección, por su `id` de `components/configuracion/secciones.tsx`.
 *
 * Configuración es solo de administradores, así que aquí no hay botones que
 * dependan del rol. Lo que antes iba en tooltips de cada botón vive aquí; lo
 * que explica un campo va en línea, bajo el campo.
 */

export const AYUDA_CONFIGURACION: ContenidoAyuda = {
  manual: "configuracion",
  intro: (
    <p>
      Todo lo que el equipo ajusta de la app, agrupado por lo que es: <b>Catálogos</b> (lo que se
      elige al crear y cerrar casos), <b>Directorio</b> (personas y empresas),{" "}
      <b>Automatizaciones</b> (cuándo actúa el sistema solo), <b>Integraciones</b> (servicios de
      terceros) y <b>Correo y empresa</b> (los datos de la casa). Cada sección tiene su propio botón
      «Ayuda».
    </p>
  ),
  secciones: [
    {
      titulo: "Cómo se guarda",
      items: [
        { label: "Switches", text: "Se aplican al momento, con su aviso. No hay que darle a «Guardar»." },
        {
          label: "Lo que se escribe",
          text: "Horas, correos, contraseñas y umbrales se guardan con el botón de su sección. Cada sección guarda solo lo suyo: cambiar una no toca las demás.",
        },
        {
          label: "Listas de los catálogos",
          text: "Agregar, renombrar, activar y eliminar se aplican al instante en cada renglón.",
        },
      ],
    },
    {
      titulo: "Renombrar, desactivar o eliminar",
      items: [
        {
          label: "Renombrar",
          text: "Marcas, modelos y tipos de solicitud y de falla se guardan en cada caso como texto: renombrarlos aquí NO cambia los casos viejos. Categorías y tipos de solución se guardan como referencia: renombrarlos cambia el nombre en todos los casos.",
        },
        {
          label: "Desactivar",
          text: "Saca algo de la lista para casos nuevos sin perder su historial. Es lo recomendado en vez de eliminar.",
        },
      ],
    },
  ],
};

// --- piezas que se repiten en los catálogos --------------------------------

const agregar = (que: string): ItemAyuda => ({
  label: "+ Agregar",
  text: `Crea ${que} con el nombre escrito. Sale gris mientras el nombre esté vacío.`,
});

const renombrar = (efecto: string): ItemAyuda => ({
  label: "Renombrar",
  text: `Convierte el nombre en un campo: Enter o salir del campo lo guarda, Escape lo cancela. ${efecto}`,
});

const desactivar = (efecto: string): ItemAyuda => ({
  label: "Desactivar / Activar",
  text: `${efecto} El renglón se ve tenue mientras está inactivo; «Activar» lo regresa.`,
});

const ordenar = (donde: string): ItemAyuda => ({
  label: "↑ / ↓",
  text: `Sube o baja el renglón: es el orden en que aparecen ${donde}. La flecha sale gris en el primero y en el último.`,
});

const TEXTO_GUARDADO =
  "Los casos que ya lo usan conservan el texto con el que se crearon.";

export const AYUDA_SECCION: Record<string, ContenidoAyuda> = {
  marcas: {
    manual: "configuracion",
    secciones: [
      {
        titulo: "Qué hace cada botón",
        items: [
          {
            label: "+ Agregar",
            text: "Crea la marca con el nombre escrito (y su proveedor, si eliges uno). Sale gris mientras el nombre esté vacío.",
          },
          {
            label: "Proveedor (en cada marca)",
            text: "A quién se le escribe por las fallas de esa marca: de ahí salen los contactos que se precargan al mandarle correo al proveedor desde un caso. Cambiarlo se guarda al instante.",
          },
          desactivar("Deja de salir al crear un caso; los casos que ya la tienen no cambian."),
          renombrar(TEXTO_GUARDADO),
          {
            label: "Eliminar",
            text: "Borra la marca y TODOS sus modelos (pide confirmación). Los casos conservan el texto de la marca. Si solo quieres sacarla de la lista, desactívala.",
          },
        ],
      },
    ],
  },
  modelos: {
    manual: "configuracion",
    secciones: [
      {
        titulo: "Qué hace cada botón",
        items: [
          {
            label: "+ Agregar",
            text: "Crea el modelo dentro de la marca elegida. Sale gris mientras falte la marca o el nombre; las marcas inactivas no se pueden elegir.",
          },
          desactivar("Deja de ofrecerse al crear un caso; los casos que ya lo tienen lo siguen mostrando."),
          renombrar(TEXTO_GUARDADO),
          {
            label: "Eliminar",
            text: "Borra el modelo del catálogo (pide confirmación). Los casos conservan el texto del modelo.",
          },
        ],
      },
    ],
  },
  categorias: {
    manual: "configuracion",
    secciones: [
      {
        titulo: "Qué hace cada botón",
        items: [
          agregar("la categoría"),
          ordenar("al crear un caso"),
          desactivar("Deja de ofrecerse al crear un caso; los casos que ya la tienen la conservan."),
          renombrar(
            "El caso guarda una clave interna, no el nombre: el nombre nuevo se ve también en los casos viejos.",
          ),
          {
            label: "Eliminar",
            text: "Solo se puede si ningún caso la usa; si tiene casos, la app no la borra y te pide desactivarla.",
          },
        ],
      },
    ],
  },
  "tipos-solicitud": {
    manual: "configuracion",
    secciones: [
      {
        titulo: "Qué hace cada botón",
        items: [
          agregar("el tipo de solicitud"),
          desactivar("Deja de ofrecerse al crear un caso de la categoría «solicitud»."),
          renombrar(TEXTO_GUARDADO),
          {
            label: "Eliminar",
            text: "Lo borra del catálogo (pide confirmación). Los casos viejos conservan el texto en su detalle.",
          },
        ],
      },
    ],
  },
  "tipos-falla": {
    manual: "configuracion",
    secciones: [
      {
        titulo: "Qué hace cada botón",
        items: [
          agregar("el tipo de falla"),
          desactivar("Deja de ofrecerse al crear un caso de la categoría «falla»."),
          renombrar(TEXTO_GUARDADO),
          {
            label: "Eliminar",
            text: "Lo borra del catálogo (pide confirmación). Los casos viejos conservan el texto en su detalle.",
          },
        ],
      },
    ],
  },
  "tipos-solucion": {
    manual: "cerrar-caso",
    secciones: [
      {
        titulo: "Qué hace cada botón",
        items: [
          agregar("el tipo de solución"),
          ordenar("al cerrar un caso"),
          desactivar("Deja de ofrecerse al cerrar un caso; el histórico conserva su tipo."),
          renombrar(
            "El caso guarda una referencia, no el texto: el nombre nuevo se ve también en los casos y equipos ya cerrados.",
          ),
          {
            label: "Eliminar",
            text: "Solo se puede si ningún caso ni equipo lo usa; si está en uso, la app no lo borra y te pide desactivarlo.",
          },
        ],
      },
    ],
  },
  contactos: {
    manual: "configuracion",
    secciones: [
      {
        titulo: "Qué hace cada botón",
        items: [
          {
            label: "Buscador",
            text: "Filtra por empresa, nombre, correo o teléfono del contacto.",
          },
          { label: "✎ (junto a la empresa)", text: "Renombra la empresa." },
          {
            label: "Asesor (✎ o «+ Asignar asesor»)",
            text: "El asesor comercial de la empresa: ✎ lo cambia y la lista también permite quitarlo. Se guarda al elegirlo. Todos los casos de la empresa lo heredan y lo ofrecen para copia en los correos.",
          },
          { label: "+ Nuevo contacto", text: "Agrega una persona a esa empresa." },
          { label: "Casos", text: "Muestra los casos en los que ha participado ese contacto." },
          {
            label: "Editar",
            text: "Cambia nombre, correo o teléfono. Es la ficha del cliente: el cambio se ve en todos sus casos.",
          },
          {
            label: "Archivar",
            text: "Lo quita de los selectores para casos nuevos; los casos viejos lo siguen mostrando. No borra nada.",
          },
          {
            label: "Archivados (N) / Reactivar",
            text: "Despliega los contactos archivados de la empresa; «Reactivar» lo devuelve a los selectores.",
          },
        ],
      },
    ],
  },
  proveedores: {
    manual: "destinatarios",
    secciones: [
      {
        titulo: "Qué hace cada botón",
        items: [
          agregar("el proveedor"),
          {
            label: "Nombre del proveedor",
            text: "Tócalo para desplegar sus contactos.",
          },
          desactivar("Lo saca de las listas para elegir."),
          renombrar(""),
          {
            label: "Eliminar (proveedor)",
            text: "Borra el proveedor y todos sus contactos (pide confirmación). Las marcas que lo tenían se quedan sin proveedor.",
          },
          {
            label: "+ Agregar contacto",
            text: "Agrega a una persona del proveedor (nombre, correo y teléfono).",
          },
          {
            label: "Hacer principal / Quitar principal",
            text: "El principal es el contacto que se preselecciona al mandarle correo al proveedor. Solo hay uno: marcar otro le quita la marca al anterior.",
          },
          {
            label: "Desactivar / Activar (contacto)",
            text: "Deja de ofrecerse al mandarle correo al proveedor.",
          },
          { label: "Eliminar (contacto)", text: "Borra ese contacto (pide confirmación)." },
        ],
      },
      {
        titulo: "Qué significa cada indicador",
        items: [
          {
            label: "Principal",
            text: "Ese contacto se preselecciona al mandarle correo a este proveedor.",
          },
        ],
      },
    ],
  },
  asesores: {
    manual: "asesores",
    secciones: [
      {
        titulo: "Qué hace cada botón",
        items: [
          agregar("el asesor"),
          {
            label: "Nombre del asesor",
            text: "Tócalo para ver sus datos y las empresas que atiende; ahí mismo se editan nombre, correo, teléfono y notas.",
          },
          {
            label: "Activo / Inactivo",
            text: "Un asesor inactivo deja de ofrecerse al elegir asesor para una empresa. Las empresas que ya lo tienen lo conservan. Se guarda al instante.",
          },
          {
            label: "Eliminar",
            text: "Sale gris mientras tenga empresas asignadas: reasígnalas primero (o déjalo inactivo). Sin empresas, se borra para siempre.",
          },
        ],
      },
      {
        titulo: "Qué significa cada indicador",
        items: [
          { label: "N clientes", text: "Cuántas empresas tiene asignadas ese asesor." },
        ],
      },
    ],
  },
  tiempos: {
    manual: "escalacion",
    secciones: [
      {
        titulo: "Qué hace cada botón",
        items: [
          {
            label: "Campos de días",
            text: "Lo que pasa al llegar a cada umbral está escrito bajo su campo. Deben ir en orden: amarillo antes que rojo, y rojo no después de admin.",
          },
          {
            label: "Por estado del caso",
            text: "Un campo vacío usa el valor general (es el número gris que ves dentro del campo).",
          },
          {
            label: "Guardar cambios",
            text: "Guarda los umbrales generales y los de cada estado juntos. Sale gris si no hay cambios o si el orden no cuadra.",
          },
        ],
      },
    ],
  },
  avisos: {
    manual: "escalacion",
    secciones: [
      {
        titulo: "Qué hace cada botón",
        items: [
          {
            label: "Switches",
            text: "Prenden o apagan cada aviso al momento. Con el aviso apagado, su hora sale gris.",
          },
          { label: "Guardar horarios", text: "Guarda las dos horas. Sale gris si no cambiaste nada." },
        ],
      },
    ],
  },
  "revision-ia": {
    manual: "correo-entrada",
    secciones: [
      {
        titulo: "Qué hace cada botón",
        items: [
          {
            label: "Revisar la bandeja con IA",
            text: "Prende o apaga al momento la revisión automática. Apagarla no borra lo que la IA ya dejó en la bandeja.",
          },
        ],
      },
    ],
  },
  jimi: {
    manual: "jimi",
    secciones: [
      {
        titulo: "Qué hace cada botón",
        items: [
          {
            label: "Revisar los tickets de Jimi cada mañana",
            text: "Prende o apaga al momento la revisión automática. Apagada, la hora sale gris.",
          },
          { label: "Guardar la hora", text: "Guarda la hora de arranque. Sale gris si no la cambiaste." },
          {
            label: "Activa / En pausa (cuenta)",
            text: "Las cuentas activas son las nuestras: lo que firmen en el portal de Jimi no se toma como respuesta de Jimi. En pausa deja de reconocerse como nuestra; lo que ya está en la bitácora no cambia.",
          },
        ],
      },
    ],
  },
  queclink: {
    manual: "proveedores",
    secciones: [
      {
        titulo: "Qué hace cada botón",
        items: [
          {
            label: "Probar la conexión",
            text: "Entra a Queclink con la cuenta guardada para comprobar que funciona. No escribe nada allá.",
          },
          {
            label: "Guardar la cuenta",
            text: "Guarda el correo y, si escribiste una, la contraseña (cifrada). Con la contraseña vacía se conserva la de antes.",
          },
          {
            label: "↻ Sincronizar ahora",
            text: "Corre esa tarea en ese momento, sin esperar a su hora. La primera vez que se traen los artículos tarda unos minutos.",
          },
          { label: "ver la guía", text: "Abre la guía «Firmware y descargas de Queclink»." },
          { label: "Guardar las horas", text: "Guarda las horas de las dos tareas diarias." },
        ],
      },
      {
        titulo: "Qué significa cada indicador",
        items: [
          {
            label: "Junto a la hora",
            text: "Cuándo corrió esa tarea por última vez.",
          },
        ],
      },
    ],
  },
  "llave-claude": {
    secciones: [
      {
        titulo: "Qué hace cada botón",
        items: [
          {
            label: "Guardar",
            text: "Comprueba la llave con Anthropic y solo si funciona la guarda (cifrada). Si falla, se queda la de antes. Aplica al momento.",
          },
          {
            label: "Probar",
            text: "Comprueba que la llave en uso responde. Sale gris si no hay llave.",
          },
          {
            label: "Quitar la llave guardada",
            text: "Solo aparece si la llave está guardada aquí. Al quitarla se vuelve a la del archivo del servidor; si no hay, la IA deja de funcionar hasta que guardes otra (la confirmación te dice cuál de los dos pasa).",
          },
        ],
      },
      {
        titulo: "Qué significa cada indicador",
        items: [
          {
            label: "guardada aquí / la del archivo del servidor / sin configurar",
            text: "De dónde sale la llave que se está usando. Junto van su principio y su final (sk-ant…f3a9), para reconocerla sin mostrarla.",
          },
        ],
      },
    ],
  },
  telegram: {
    manual: "notificaciones",
    secciones: [
      {
        titulo: "Qué hace cada botón",
        items: [
          {
            label: "Guardar",
            text: "Valida el token con Telegram, lo guarda cifrado y conecta el bot a la app en el mismo paso.",
          },
          {
            label: "Probar",
            text: "Comprueba el token con Telegram y, si tienes tu Telegram vinculado, te manda un mensaje de prueba.",
          },
          {
            label: "Eliminar token",
            text: "Borra el token y desconecta el bot. Las vinculaciones de cada quien se conservan: si guardas otra vez el token del mismo bot, siguen funcionando.",
          },
        ],
      },
    ],
  },
  "correo-entrante": {
    manual: "correo-entrada",
    secciones: [
      {
        titulo: "Qué hace cada botón",
        items: [
          {
            label: "Guardar los buzones",
            text: "Guarda la lista. Sale gris si no la cambiaste.",
          },
        ],
      },
    ],
  },
  buzon: {
    manual: "correo-entrada",
    secciones: [
      {
        titulo: "Qué hace cada botón",
        items: [
          {
            label: "Revisar los buzones automáticamente",
            text: "Prende o apaga al momento la revisión. El botón «Revisar mi correo» de la Bandeja funciona igual.",
          },
          {
            label: "Guardar el horario",
            text: "Guarda cada cuántos minutos y entre qué horas se revisa.",
          },
          {
            label: "Usar IA…",
            text: "Prende o apaga al momento la ayuda de la IA para los correos que las reglas no clasifican.",
          },
        ],
      },
    ],
  },
  empresa: {
    secciones: [
      {
        titulo: "Qué hace cada botón",
        items: [
          {
            label: "Guardar cambios",
            text: "Guarda el nombre y el correo de soporte. Sale gris si no cambiaste nada o si el nombre quedó vacío.",
          },
        ],
      },
    ],
  },
  "mensajes-rapidos": {
    manual: "notificaciones",
    secciones: [
      {
        titulo: "Qué hace cada botón",
        items: [
          agregar("el mensaje"),
          {
            label: "Desactivar / Activar",
            text: "Lo oculta o lo muestra en la ventana «Pasar a un compañero». No lo borra.",
          },
          { label: "Editar", text: "Cambia el texto; «Guardar» lo aplica y «Cancelar» lo deja como estaba." },
          { label: "Eliminar", text: "Borra el mensaje (pide confirmación)." },
        ],
      },
    ],
  },
};
